import type { ActivityItem, ActivityStatus } from './activity-thread-types'
import { isFinished } from './activity-thread-types'

/** One step shown on its own. */
interface StepEntry {
  type: 'step'
  id: string
  item: ActivityItem
}

/** Consecutive web searches shown as one step. */
interface SearchGroupEntry {
  type: 'searches'
  id: string
  searches: ActivityItem[]
}

export type ActivityEntry = StepEntry | SearchGroupEntry

/** Folds each run of consecutive web-search steps into one group; any other step between two searches breaks the run. */
export function groupActivity(items: readonly ActivityItem[]): ActivityEntry[] {
  const entries: ActivityEntry[] = []
  for (const item of items) {
    const last = entries[entries.length - 1]
    if (item.kind !== 'web-search') {
      entries.push({ type: 'step', id: item.id, item })
    } else if (last?.type === 'searches') {
      last.searches.push(item)
    } else {
      entries.push({ type: 'searches', id: item.id, searches: [item] })
    }
  }
  return entries
}

/** The status one group shows: running while any search runs or some are done and some wait, failed only when every search failed. */
export function groupStatus(searches: readonly ActivityItem[]): ActivityStatus {
  if (searches.every((search) => isFinished(search.status))) {
    return searches.every((search) => search.status === 'failed') ? 'failed' : 'complete'
  }
  const started = searches.some((search) => search.status !== 'pending')
  return started ? 'active' : 'pending'
}

/** The text a web search looked for, when its label carried one. */
export function searchQuery(item: ActivityItem): string | undefined {
  const query = item.labelParams?.query
  return typeof query === 'string' && query.trim() ? query.trim() : undefined
}
